import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminHeaders } from '../utils/adminHeaders';
import {
    Typography,
    Input,
    Button,
    Card,
    CardContent,
    Sheet,
    Divider,
    Grid,
    Box,
    Stack,
    Chip,
    Alert,
} from '@mui/joy';
import {
    Search,
    DeviceHub,
    Memory,
    Update,
    Person,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const DevicesManagement = () => {
    const { session } = useAuth();
    const [hwIdQuery, setHwIdQuery] = useState('');
    const [deviceResults, setDeviceResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    const searchDevices = async () => {
        if (!hwIdQuery.trim()) {
            setMessage('Please enter a hardware ID to search');
            return;
        }

        try {
            setLoading(true);
            setMessage('');
            
            const res = await fetch(`${API_BASE}/search/device?hardware_id=${encodeURIComponent(hwIdQuery)}`, {
                headers: adminHeaders(session),
            });
            
            if (!res.ok) {
                throw new Error(`Search failed: ${res.status}`);
            }
            
            const devices = await res.json();
            setDeviceResults(devices);
            setMessage(devices.length ? '' : 'No devices found');
        } catch (error) {
            console.error('Search error:', error);
            setMessage('Failed to search devices. Please try again.');
            setDeviceResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            searchDevices();
        }
    };

    const clearSearch = () => {
        setHwIdQuery('');
        setDeviceResults([]);
        setMessage('');
    };

    const handleDeviceClick = (device) => {
        navigate(`/hub/${device.id}`);
    };

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <DeviceHub sx={{ fontSize: 32, color: 'warning.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            Device Management
                        </Typography>
                        <Typography level="body-lg" textColor="neutral.600">
                            Search, monitor, and manage connected devices
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Search Section */}
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Typography level="h4" sx={{ mb: 3 }}>
                        Search Devices
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="end">
                        <Grid xs={12} md={8}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                                Hardware ID
                            </Typography>
                            <Input
                                placeholder="Enter device hardware ID"
                                value={hwIdQuery}
                                onChange={(e) => setHwIdQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                startDecorator={<Memory />}
                                size="lg"
                                disabled={loading}
                            />
                        </Grid>
                        <Grid xs={12} md={4}>
                            <Stack direction="row" spacing={2}>
                                <Button 
                                    onClick={searchDevices} 
                                    variant="solid"
                                    color="warning"
                                    startDecorator={<Search />}
                                    loading={loading}
                                    disabled={!hwIdQuery.trim()}
                                    size="lg"
                                    sx={{ flex: 1 }}
                                >
                                    Search
                                </Button>
                                {(hwIdQuery || deviceResults.length > 0) && (
                                    <Button 
                                        onClick={clearSearch}
                                        variant="outlined"
                                        color="neutral"
                                        size="lg"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>

                    {/* Quick Actions */}
                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography level="body-sm" sx={{ mb: 2 }} textColor="neutral.600">
                            Quick Actions
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                startDecorator={<Update />}
                                disabled // Will be enabled when you implement this feature
                            >
                                Bulk Update
                            </Button>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            {/* Messages */}
            {message && (
                <Alert 
                    color={deviceResults.length > 0 ? "success" : "warning"} 
                    sx={{ mb: 3 }}
                >
                    {message}
                </Alert>
            )}

            {/* Results Section */}
            {deviceResults.length > 0 && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography level="h4">
                            Search Results
                        </Typography>
                        <Chip color="warning" variant="soft">
                            {deviceResults.length} device{deviceResults.length === 1 ? '' : 's'} found
                        </Chip>
                    </Stack>
                    
                    <Grid container spacing={3}>
                        {deviceResults.map((device) => (
                            <Grid key={device.id} xs={12} md={6} lg={4}>
                                <Card 
                                    onClick={() => handleDeviceClick(device)}
                                    variant="outlined" 
                                    sx={{ 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 'md',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'warning.softBg',
                                                        color: 'warning.500',
                                                    }}
                                                >
                                                    <DeviceHub sx={{ fontSize: 20 }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography level="title-md" sx={{ mb: 0.5 }}>
                                                        {device.name || `Device ${device.id}`}
                                                    </Typography>
                                                    <Typography 
                                                        level="body-sm" 
                                                        textColor="neutral.500"
                                                        sx={{ 
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        HW ID: {device.hardware_id}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            
                                            <Divider />
                                            
                                            <Grid container spacing={1}>
                                                <Grid xs={6}>
                                                    <Typography level="body-xs" textColor="neutral.600">
                                                        Owner ID
                                                    </Typography>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        {device.owner_id}
                                                    </Typography>
                                                </Grid>
                                                <Grid xs={6}>
                                                    <Typography level="body-xs" textColor="neutral.600">
                                                        Log Level
                                                    </Typography>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        {device.log_level}
                                                    </Typography>
                                                </Grid>
                                                <Grid xs={12}>
                                                    <Typography level="body-xs" textColor="neutral.600">
                                                        Created
                                                    </Typography>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        {new Date(device.created_at).toLocaleString()}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Empty State */}
            {!loading && deviceResults.length === 0 && !message && (
                <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                    <DeviceHub sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="h4" sx={{ mb: 1 }}>
                        Search for Devices
                    </Typography>
                    <Typography level="body-sm" textColor="neutral.500">
                        Enter a device hardware ID above to start searching
                    </Typography>
                </Card>
            )}
        </Sheet>
    );
};

export default DevicesManagement; 